import { differenceInCalendarMonthsUTC, startOfTodayUTC } from "~/utils/datetime"

export const DEFAULT_MATCH_QUALITY = 0.6
export const MIN_MATCH_QUALITY = 0.1
export const MAX_MATCH_QUALITY = 0.7
// AI suggestions shown per variant (getNearestNeighbors, Estimated sales). Every path that
// commits or derives from "the visible AI set" must apply this cap and the brandMatchQuality
// threshold together, or what gets applied diverges from what the planner saw.
export const NEIGHBOR_LIMIT = 5

const MIN_MATCH_SALES_HISTORY_MONTHS = 12

function hasMinSalesHistory(soldSince: Date | null | undefined): boolean {
  if (soldSince == null) return false
  return (
    differenceInCalendarMonthsUTC(startOfTodayUTC(), soldSince) >= MIN_MATCH_SALES_HISTORY_MONTHS
  )
}

export function displayableMatchScore(
  score: number | null | undefined,
  soldSince: Date | null | undefined,
): number | null {
  if (score == null) return null
  return hasMinSalesHistory(soldSince) ? score : null
}

/**
 * Whether a variant's matches are entirely user-confirmed, which renders the score badge gray:
 * the average similarity of hand-picked matches isn't a system quality signal, so colouring it
 * green/amber/red would read as an endorsement the pipeline never made.
 *
 * Shared so every badge applies the same rule — it previously lived at a single call site, and the
 * variant page's badge stayed coloured for custom matches as a result.
 */
export const isAllCustomMatches = (neighbors: { metadata: { source: string } }[] | undefined) =>
  neighbors != null &&
  neighbors.length > 0 &&
  neighbors.every((n) => n.metadata.source === "CustomMatch")

export const DEFAULT_MIN_MATCH_COUNT = 2
export const MIN_MIN_MATCH_COUNT = 1
export const MAX_MIN_MATCH_COUNT = 10

interface ShouldShowMatchCountAlertOptions {
  baselineMatchCount?: number
  minMatchCountThreshold?: number
}

// Fires when a neighbor baseline rests on fewer matches than the threshold. The default
// of 2 reproduces the legacy single-match alert (baselineMatchCount < 2 fires on exactly 1 match).
export function shouldShowMatchCountAlert({
  baselineMatchCount,
  minMatchCountThreshold = DEFAULT_MIN_MATCH_COUNT,
}: ShouldShowMatchCountAlertOptions) {
  return baselineMatchCount != null && baselineMatchCount < minMatchCountThreshold
}

export function getMatchQualityPercentage(matchQuality = DEFAULT_MATCH_QUALITY) {
  return `${Math.round(matchQuality * 100)}%`
}

export const MATCH_QUALITY_LABELS = ["Great Match", "Good Match", "Okay Match", "Bad Match"]

interface GetMatchQualityLabelOptions {
  similarity: number
  brandMatchQuality?: number
}

export function getMatchQualityLabel({
  similarity,
  brandMatchQuality = DEFAULT_MATCH_QUALITY,
}: GetMatchQualityLabelOptions): string {
  if (similarity >= brandMatchQuality + 0.3) return "Great Match"
  if (similarity >= brandMatchQuality + 0.1) return "Good Match"
  if (similarity >= brandMatchQuality) return "Okay Match"
  return "Bad Match"
}

interface GetMatchQualityForBadgeOptions {
  similarity: number
  brandMatchQuality?: number
}

export function getMatchQualityForBadge({
  similarity,
  brandMatchQuality = DEFAULT_MATCH_QUALITY,
}: GetMatchQualityForBadgeOptions): {
  text: string
  variant: "green" | "yellow" | "red"
} {
  const percentage = getMatchQualityPercentage(similarity)
  const label = getMatchQualityLabel({ similarity, brandMatchQuality })
  const variant = label === "Okay Match" ? "yellow" : label === "Bad Match" ? "red" : "green"
  return { text: `${label} (${percentage})`, variant }
}

interface ShouldShowSimilarityAlertOptions {
  similarity: number
  brandMatchQuality?: number
}

export function shouldShowSimilarityAlert({
  similarity,
  brandMatchQuality = DEFAULT_MATCH_QUALITY,
}: ShouldShowSimilarityAlertOptions) {
  return similarity < brandMatchQuality
}
